import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define the shape of our context state
interface CurrencyContextType {
  dealCurrency: string;
  currencySymbol: string;
  setDealCurrency: (currency: string) => void;
  setCurrencySymbol: (symbol: string) => void;
  updateCurrency: (currency: string) => void; // Helper function to update both at once
}

// Create the context with a default value
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Currency symbols mapping
export const currencySymbolsMap: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  INR: '₹',
  // Add more currencies as needed
};

// Provider component
interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [dealCurrency, setDealCurrency] = useState<string>('USD');
  const [currencySymbol, setCurrencySymbol] = useState<string>('$');

  // Helper function to update both currency and symbol
  const updateCurrency = (currency: string) => {
    setDealCurrency(currency);
    setCurrencySymbol(currencySymbolsMap[currency] || '$'); // Default to $ if symbol not found
  };

  return (
    <CurrencyContext.Provider value={{
      dealCurrency,
      currencySymbol,
      setDealCurrency,
      setCurrencySymbol,
      updateCurrency
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook for using the currency context
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
